import React from 'react';
import { Box, CircularProgress, Stack, Typography, styled } from '@mui/material';
import { DateRangePickerProps } from 'components/DateRangePicker/DateRangePicker';
import { BloodPressureChart } from 'components/BloodPressureWidget/BloodPressureChart';
import { ReadingRow } from 'components/ProgressTab/WidgetDetail/WidgetReadingList';
import { WidgetDetailLayout } from 'components/ProgressTab/WidgetDetail/WidgetDetailLayout';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';

const Summary = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  gap: theme.spacing(5),
  // Equal space above (date picker) and below (chart).
  marginBlock: theme.spacing(3),
  [theme.breakpoints.up('sm')]: { gap: theme.spacing(8), marginBlock: theme.spacing(4) },
}));

// Number range and label share one centered axis.
const Stat = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
});

const StatValue = styled(Typography)(({ theme }) => ({
  ...theme.typography.title3,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title1,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const StatUnit = styled('span')(({ theme }) => ({
  ...theme.typography.subheadline,
  fontWeight: theme.typography.fontWeightRegular,
}));

const StatLabel = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  color: theme.palette.grey[800],
  ...theme.typography.subheadline,
  [theme.breakpoints.up('sm')]: { ...theme.typography.callout },
}));

// Same markers as the chart: systolic is a filled dot, diastolic a hollow ring.
const Marker = styled('span', { shouldForwardProp: (prop) => prop !== 'hollow' })<{
  hollow?: boolean;
}>(({ theme, hollow }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  boxSizing: 'border-box',
  border: `2px solid ${theme.palette.primary.main}`,
  backgroundColor: hollow ? theme.palette.common.white : theme.palette.primary.main,
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(0, 4) },
}));

export interface BloodPressureDetailsViewProps {
  dateRange: DateRangePickerProps;
  dates: string[];
  systolic: number[];
  diastolic: number[];
  systolicRange: string;
  diastolicRange: string;
  units: string;
  readings: ReadingRow[];
  isLoading?: boolean;
  isError?: boolean;
  onBack: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
}

export const BloodPressureDetailsView = ({
  dateRange,
  dates,
  systolic,
  diastolic,
  systolicRange,
  diastolicRange,
  units,
  readings,
  isLoading = false,
  isError = false,
  onBack,
  onDownload,
  isDownloading = false,
}: BloodPressureDetailsViewProps) => {
  const { translation } = useTranslationContext();

  const renderBody = () => {
    if (isLoading) {
      return (
        <Stack alignItems="center" p={6}>
          <CircularProgress aria-label={translation('blood_pressure')} />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Stack alignItems="center" p={6}>
          <Typography variant="footnote">{translation('data_unable_to_load')}</Typography>
        </Stack>
      );
    }

    return (
      <ChartWrapper>
        <BloodPressureChart
          dates={dates}
          systolic={systolic}
          diastolic={diastolic}
          height={300}
          margin={{ top: 16, right: 8, bottom: 24, left: 40 }}
        />
      </ChartWrapper>
    );
  };

  return (
    <WidgetDetailLayout
      title={translation('blood_pressure')}
      onBack={onBack}
      dateRange={{
        ...dateRange,
        previousAriaLabel: translation('back_1_week_accessibility'),
        nextAriaLabel: translation('forward_1_week_accessibility'),
      }}
      summary={
        !isLoading &&
        !isError && (
          <Summary>
            <Stat>
              <StatValue>
                {systolicRange} <StatUnit>{units}</StatUnit>
              </StatValue>
              <StatLabel>
                <Marker aria-hidden />
                {translation('systolic_range')}
              </StatLabel>
            </Stat>
            <Stat>
              <StatValue>
                {diastolicRange} <StatUnit>{units}</StatUnit>
              </StatValue>
              <StatLabel>
                <Marker hollow aria-hidden />
                {translation('diastolic_range')}
              </StatLabel>
            </Stat>
          </Summary>
        )
      }
      readings={readings}
      onDownload={onDownload}
      isDownloading={isDownloading}
    >
      {renderBody()}
    </WidgetDetailLayout>
  );
};
